import { ComponentType, ElementType, forwardRef, ReactNode } from "react"
import styled, { PolymorphicComponentProps } from "styled-components"
import { Typography } from "../../typography"
import { DeprecatedSpinner } from "../spinner"

type ButtonSize = "small" | "medium" | "large"
type ButtonVariant = "primary" | "secondary" | "tertiary" | "round" | "danger" | "underline"

export type DeprecatedButtonPropsBase = {
  leftIcon?: ComponentType
  rightIcon?: ComponentType
  centerIcon?: ComponentType
  onClick?: (e: React.MouseEvent) => void
}

export type DeprecatedButtonPropsOtherVariant = DeprecatedButtonPropsBase & {
  $variant?: Exclude<ButtonVariant, "underline">
  size?: ButtonSize
  isLoading?: boolean
  disabled?: boolean
  $fullWidth?: boolean
}

export type DeprecatedButtonPropsUnderlineVariant = DeprecatedButtonPropsBase & {
  $variant: "underline"
  disabled?: undefined
  size?: ButtonSize
  isLoading?: boolean
}

export type DeprecatedButtonProps<C extends ElementType> = PolymorphicComponentProps<
  "web",
  DeprecatedButtonPropsOtherVariant | DeprecatedButtonPropsUnderlineVariant,
  C,
  C
>

const ButtonContainer = styled(Typography).attrs<{
  $buttonVariant?: ButtonVariant
  as?: ElementType
  size?: ButtonSize
  $fullWidth?: boolean
}>(({ $buttonVariant, as, size }) => ({
  $buttonVariant: $buttonVariant ?? "primary",
  size: size ?? "small",
  $variant: "body-4-medium",
  as: as ?? "button",
}))`
  min-width: ${({ $buttonVariant }) =>
    $buttonVariant === "round" ? "2.5rem" : $buttonVariant === "underline" ? "0" : "8.125rem"};
  width: ${({ $buttonVariant, $fullWidth }) => ($buttonVariant === "round" ? "2.5rem" : $fullWidth ? "100%" : "auto")};
  height: ${({ $buttonVariant, size }) =>
    $buttonVariant === "round" ? "2.5rem" : size === "small" ? "2rem" : size === "medium" ? "2.5rem" : "3rem"};
  border-radius: ${({ $buttonVariant }) => ($buttonVariant === "underline" ? "0" : "0.5rem")};
  border: 0.25rem solid transparent;
  outline: ${({ $buttonVariant, theme }) =>
    $buttonVariant === "tertiary" || $buttonVariant === "round" || $buttonVariant === "danger"
      ? `1px solid ${theme.colors.grey.$200}`
      : "unset"};
  box-shadow: ${({ $buttonVariant, theme }) =>
    $buttonVariant === "tertiary" || $buttonVariant === "round" || $buttonVariant === "danger"
      ? theme.shadows.light
      : $buttonVariant === "underline"
        ? `inset 0 -2px 0px ${theme.colors.grey.$300}`
        : "unset"};
  box-sizing: border-box;
  display: inline-flex;
  user-select: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: ${({ $buttonVariant }) =>
    $buttonVariant === "round" || $buttonVariant === "underline" ? "0" : " 0.25rem 0.75rem"};
  background: ${({ $buttonVariant, theme }) =>
    $buttonVariant === "primary"
      ? theme.colors.black.$900
      : $buttonVariant === "secondary"
        ? theme.colors.grey.$300
        : $buttonVariant === "tertiary" ||
            $buttonVariant === "round" ||
            $buttonVariant === "danger" ||
            $buttonVariant === "underline"
          ? theme.colors.white
          : theme.colors.black.$100};
  color: ${({ $buttonVariant, theme }) =>
    $buttonVariant === "primary"
      ? theme.colors.white
      : $buttonVariant === "secondary"
        ? theme.colors.grey.$500
        : $buttonVariant === "tertiary" || $buttonVariant === "round" || $buttonVariant === "underline"
          ? theme.colors.grey.$600
          : $buttonVariant === "danger"
            ? theme.colors.danger.$200
            : theme.colors.black.$900};

  &:focus {
    box-shadow: none;
    outline: ${({ $buttonVariant, theme }) => $buttonVariant !== "underline" && `2px solid ${theme.colors.grey.$400}`};
    border: ${({ $buttonVariant, theme }) =>
      $buttonVariant === "underline" && `.25rem solid ${theme.colors.grey.$400}`};
    border-radius: ${({ $buttonVariant }) => $buttonVariant === "underline" && "0.5rem"};
  }

  &:hover {
    color: ${({ $buttonVariant, theme }) =>
      $buttonVariant === "primary" || $buttonVariant === "tertiary" || $buttonVariant === "round"
        ? theme.colors.white
        : $buttonVariant === "danger"
          ? theme.colors.danger.$200
          : $buttonVariant === "underline"
            ? theme.colors.grey.$400
            : theme.colors.grey.$500};
    background: ${({ $buttonVariant, theme }) =>
      $buttonVariant === "primary" || $buttonVariant === "tertiary" || $buttonVariant === "round"
        ? theme.colors.black.$1000
        : $buttonVariant === "danger"
          ? theme.colors.grey.$300
          : $buttonVariant === "underline"
            ? theme.colors.white
            : theme.colors.grey.$400};
  }

  &:active:enabled {
    box-shadow: none;
    outline: ${({ $buttonVariant, theme }) => $buttonVariant !== "underline" && `2px solid ${theme.colors.grey.$400}`};
    border: ${({ $buttonVariant, theme }) =>
      $buttonVariant === "underline" && `.25rem solid ${theme.colors.grey.$400}`};
    background: ${({ $buttonVariant, theme }) =>
      $buttonVariant === "underline" ? theme.colors.white : theme.colors.grey.$600};
    color: ${({ $buttonVariant, theme }) =>
      $buttonVariant === "underline" ? theme.colors.grey.$300 : theme.colors.grey.$400};
  }

  &:disabled,
  &[aria-disabled="true"] {
    outline: none;
    box-shadow: none;
    background-color: ${({ theme }) => theme.colors.grey.$300};
    color: ${({ theme }) => theme.colors.grey.$400};
    cursor: not-allowed;
  }
`

const ButtonContent = styled.div<{ $isLoading: boolean }>`
  visibility: ${({ $isLoading }) => ($isLoading ? "hidden" : "visible")};
  display: inherit;
  gap: 0.5rem;
`

const StyledSpinner = styled(DeprecatedSpinner)`
  position: absolute;
`

type ButtonType = <C extends ElementType = "button">(
  props: DeprecatedButtonProps<C> & { ref?: React.ComponentPropsWithRef<C>["ref"] },
) => ReactNode | null

export const DeprecatedButton: ButtonType = forwardRef(
  <C extends ElementType = "button">(
    {
      children,
      leftIcon,
      rightIcon,
      centerIcon,
      isLoading,
      disabled,
      onClick,
      $variant,
      ...rest
    }: DeprecatedButtonProps<C>,
    ref?: React.ComponentPropsWithRef<C>["ref"],
  ) => {
    const LeftIcon: ComponentType = leftIcon ? leftIcon : () => <></>
    const RightIcon: ComponentType = rightIcon ? rightIcon : () => <></>
    const CenterIcon: ComponentType = centerIcon ? centerIcon : () => <></>
    return (
      <ButtonContainer
        {...rest}
        ref={ref}
        $buttonVariant={$variant}
        disabled={disabled}
        aria-disabled={disabled}
        aria-busy={isLoading}
        onClick={(e: React.MouseEvent) => {
          if (disabled || isLoading) {
            e.preventDefault()
            e.stopPropagation()

            return
          }

          ;(onClick as (e: React.MouseEvent) => void)?.(e)
        }}
      >
        {isLoading && <StyledSpinner />}
        <ButtonContent $isLoading={!!isLoading}>
          <CenterIcon />
          <LeftIcon />
          {children}
          <RightIcon />
        </ButtonContent>
      </ButtonContainer>
    )
  },
)
